if (!customElements.get('bf-product-form')) {
  customElements.define(
    'bf-product-form',
    class BFProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-bf-add-to-cart]');
        this.submitLabel = this.querySelector('[data-bf-add-to-cart-label]');
        this.buyNowButton = this.querySelector('[data-bf-buy-now]');
        this.status = this.closest('[data-bf-product-main]')?.querySelector('[data-bf-product-form-status]');
        this.productInfo = this.closest('bf-product-info');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.loading = false;
        this.checkoutLoading = false;
        this.resetTimer = null;
        this.onSubmit = (event) => this.handleSubmit(event);
        this.onPageShow = () => this.resetCheckoutLoading();

        this.form?.addEventListener('submit', this.onSubmit);
        window.addEventListener('pageshow', this.onPageShow);
        if (this.cart) this.submitButton?.setAttribute('aria-haspopup', 'dialog');
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.resetTimer) window.clearTimeout(this.resetTimer);
      }

      async handleSubmit(event) {
        const submitter = event.submitter;
        if (submitter?.matches('[data-bf-buy-now]')) {
          if (this.checkoutLoading) {
            event.preventDefault();
            return;
          }
          this.clearStatus();
          this.setCheckoutLoading();
          return;
        }
        if (submitter && !submitter.matches('[data-bf-add-to-cart]')) return;

        event.preventDefault();
        if (!this.form || !this.submitButton || this.loading || this.submitButton.disabled) return;

        this.clearStatus();
        this.setLoading(true);

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 30000);

        try {
          if (this.cart?.getSectionsToRender) {
            formData.append(
              'sections',
              this.cart
                .getSectionsToRender()
                .map((section) => section.id)
                .join(',')
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(window.routes?.cart_add_url || this.form.action, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
            signal: controller.signal,
          });
          const payload = await response.json();

          if (!response.ok || payload.status) {
            const error = new Error(payload.description || payload.message || this.config.serverErrorLabel);
            error.payload = payload;
            throw error;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bf-product-form',
              productVariantId: variantId,
              cartData: payload,
            });
          }

          if (this.cart?.renderContents && payload.sections) {
            // Match the native product form when the first item leaves an empty cart.
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(payload);
          } else if (window.routes?.cart_url) {
            window.location.assign(window.routes.cart_url);
            return;
          }

          this.showAdded();
        } catch (error) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'bf-product-form',
              productVariantId: variantId,
              errors: error.payload?.errors || error.message,
              message: error.payload?.message || error.message,
            });
          }

          const message = error.name === 'AbortError' ? this.config.networkErrorLabel : error.message;
          this.showError(message || this.config.serverErrorLabel);
        } finally {
          window.clearTimeout(timeout);
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        this.loading = loading;
        this.submitButton?.classList.toggle('is-loading', loading);
        this.submitButton?.setAttribute('aria-busy', String(loading));
        this.form?.setAttribute('aria-busy', String(loading));

        if (loading) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.buyNowButton) {
            this.buyNowButton.disabled = true;
            this.buyNowButton.setAttribute('aria-disabled', 'true');
          }
          if (this.submitLabel) this.submitLabel.textContent = this.config.addingLabel;
          if (this.status) this.status.textContent = this.config.addingLabel;
          return;
        }

        const variant = this.productInfo?.currentVariant;
        const canPurchase = Boolean(variant?.available);
        this.submitButton.disabled = !canPurchase;
        this.submitButton.setAttribute('aria-disabled', String(!canPurchase));
        this.submitButton.removeAttribute('aria-busy');
        this.form?.removeAttribute('aria-busy');
        if (this.buyNowButton) {
          this.buyNowButton.disabled = !canPurchase;
          this.buyNowButton.setAttribute('aria-disabled', String(!canPurchase));
        }

        if (!this.submitButton.classList.contains('is-added') && this.submitLabel) {
          this.submitLabel.textContent = canPurchase
            ? this.config.addToCartLabel
            : variant
              ? this.config.soldOutLabel
              : this.config.unavailableLabel;
        }
      }

      setCheckoutLoading() {
        if (!this.buyNowButton) return;
        this.checkoutLoading = true;
        this.buyNowButton.classList.add('is-loading');
        this.buyNowButton.setAttribute('aria-busy', 'true');
        this.buyNowButton.setAttribute('aria-disabled', 'true');
        this.form?.setAttribute('aria-busy', 'true');
      }

      resetCheckoutLoading() {
        if (!this.checkoutLoading || !this.buyNowButton) return;
        this.checkoutLoading = false;
        this.buyNowButton.classList.remove('is-loading');
        this.buyNowButton.removeAttribute('aria-busy');
        this.form?.removeAttribute('aria-busy');
        const canPurchase = Boolean(this.productInfo?.currentVariant?.available);
        this.buyNowButton.setAttribute('aria-disabled', String(!canPurchase));
      }

      showAdded() {
        if (!this.submitButton || !this.submitLabel) return;
        this.submitButton.classList.add('is-added');
        this.submitLabel.textContent = this.config.addedLabel;
        if (this.status) this.status.textContent = this.config.addedLabel;

        if (this.resetTimer) window.clearTimeout(this.resetTimer);
        this.resetTimer = window.setTimeout(() => {
          this.submitButton?.classList.remove('is-added');
          if (this.submitLabel) this.submitLabel.textContent = this.config.addToCartLabel;
        }, 1600);
      }

      showError(message) {
        this.submitButton?.classList.add('is-error');
        if (!this.status) return;
        this.status.textContent = message;
        this.status.classList.remove('visually-hidden');
        this.status.classList.add('is-error');
        this.status.setAttribute('role', 'alert');
      }

      clearStatus() {
        this.submitButton?.classList.remove('is-error');
        if (!this.status) return;
        this.status.textContent = '';
        this.status.classList.add('visually-hidden');
        this.status.classList.remove('is-error');
        this.status.setAttribute('role', 'status');
      }

      get config() {
        return this.productInfo?.config || {
          addToCartLabel: 'Add to cart',
          addingLabel: 'Adding…',
          addedLabel: 'Added',
          soldOutLabel: 'Sold out',
          unavailableLabel: 'Unavailable',
          serverErrorLabel: 'We could not add this item. Please try again.',
          networkErrorLabel: 'The connection took too long. Check your network and try again.',
        };
      }
    }
  );
}
